import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, UploadCloud, Trash2, Film, DollarSign, BarChart3, Settings, Check, Users, ListVideo, Ticket, Megaphone, Bell } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const posterUrl = (p) => (p?.startsWith("http") ? p : p ? `${BACKEND_URL}/api/files/${p}` : "");
const CHUNK = 4 * 1024 * 1024;
const TIERS = [["ppv", "Pay-per-view"], ["rent_day", "Rent · Day"], ["rent_week", "Rent · Week"], ["rent_month", "Rent · Month"], ["rent_year", "Rent · Year"], ["subscription", "Subscription"]];
const TABS = [["upload", "Content & Upload", Film], ["pricing", "Pricing", DollarSign], ["analytics", "Analytics", BarChart3], ["settings", "Streaming Settings", Settings]];

const STUDIO_TABS = [["content", "Content", Film], ["playlists", "Playlists & Showlists", ListVideo], ["screenings", "Test Screenings", Ticket], ["ads", "Ads Settings", Megaphone], ["subscriptions", "Subscriptions", Bell]];

function PlaylistsTab({ items }) {
  const [lists, setLists] = useState([]);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("playlist");
  const [picked, setPicked] = useState([]);
  const load = async () => setLists((await api.playlists()).playlists || []);
  useEffect(() => { load(); }, []);
  const toggle = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const create = async () => { if (!title.trim()) return toast.error("Title required"); await api.createPlaylist({ title, kind, content_ids: picked }); setTitle(""); setPicked([]); await load(); toast.success(`${kind} created`); };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="playlists-tab">
      <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-5 space-y-3">
        <div className="mono text-kinetic">CREATE PLAYLIST / SHOWLIST</div>
        <Input data-testid="playlist-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title (e.g. Festival Favourites)" className="bg-black border-white/15 rounded-sm" />
        <div className="flex gap-1">
          {[["playlist", "PLAYLIST"], ["showlist", "SHOWLIST"]].map(([k, l]) => (
            <button key={k} data-testid={`playlist-kind-${k}`} onClick={() => setKind(k)} className={`mono px-3 h-8 rounded-sm border ${kind === k ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55"}`}>{l}</button>
          ))}
        </div>
        <div className="max-h-52 overflow-y-auto space-y-1">
          {items.map(c => (
            <button key={c.id} data-testid={`playlist-pick-${c.id}`} onClick={() => toggle(c.id)} className={`w-full text-left flex items-center gap-2 border rounded-sm px-3 py-2 ${picked.includes(c.id) ? "border-kinetic bg-kinetic/10" : "border-white/10"}`}>
              <span className={`w-4 h-4 rounded-sm border flex items-center justify-center ${picked.includes(c.id) ? "bg-kinetic border-kinetic" : "border-white/30"}`}>{picked.includes(c.id) && <Check className="w-3 h-3 text-black" />}</span>
              <span className="text-sm">{c.title}</span>
            </button>
          ))}
        </div>
        <Button data-testid="playlist-create" onClick={create} className="kinetic-btn rounded-sm w-full">Create {kind}</Button>
      </div>
      <div className="space-y-2">
        {lists.length === 0 && <div className="border border-dashed border-white/15 rounded-lg p-10 text-center mono text-white/40">NO PLAYLISTS YET</div>}
        {lists.map(l => (
          <div key={l.id} className="border border-white/10 bg-[#0C0C0C] rounded-lg p-4 flex items-center justify-between" data-testid={`playlist-${l.id}`}>
            <div><div className="font-sans font-bold">{l.title}</div><div className="mono text-white/40 mt-0.5">{l.kind.toUpperCase()} · {l.content_ids?.length || 0} TITLES</div></div>
            <Trash2 data-testid={`playlist-del-${l.id}`} onClick={() => api.deletePlaylist(l.id).then(load)} className="w-4 h-4 text-white/25 hover:text-coral" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ScreeningsTab() {
  const [reqs, setReqs] = useState([]);
  useEffect(() => { api.screeningRequestsReceived().then(r => setReqs(r.requests || [])); }, []);
  return (
    <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-5" data-testid="screenings-tab">
      <div className="mono text-kinetic mb-3">TEST / PAID SCREENING REQUESTS</div>
      {reqs.length === 0 ? <div className="mono text-white/40 text-center py-10">NO REQUESTS YET · ENABLE "PAID SCREENING" ON A TITLE'S PRICING</div> : (
        <div className="space-y-2">
          {reqs.map(r => (
            <div key={r.id} className="border border-white/10 rounded-sm p-3" data-testid={`screening-${r.id}`}>
              <div className="flex items-center justify-between"><div className="font-sans font-bold">{r.name}</div><span className="mono text-kinetic">{(r.status || "requested").toUpperCase()}</span></div>
              <div className="mono text-white/40 mt-1">{r.content_title} · {r.email} {r.preferred_date ? `· ${r.preferred_date}` : ""}</div>
              {r.message && <p className="text-white/70 text-sm mt-1">{r.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdsTab({ items, reload }) {
  const setAdsFree = async (c, v) => { const pricing = { ...(c.pricing || {}), ads_free: v }; await api.updateContent(c.id, { pricing }); await reload(); };
  return (
    <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-5 space-y-3" data-testid="ads-tab">
      <div className="mono text-kinetic">ADS SETTINGS</div>
      <p className="mono text-white/50">Toggle an ad-free experience per title. Ad-free titles never show pre-roll or mid-roll ads.</p>
      {items.length === 0 && <div className="mono text-white/40 text-center py-8">NO CONTENT YET</div>}
      {items.map(c => (
        <div key={c.id} className="flex items-center justify-between border border-white/10 rounded-sm px-4 py-3" data-testid={`ads-${c.id}`}>
          <div className="font-sans font-bold">{c.title}</div>
          <label className="flex items-center gap-2 mono text-white/60"><Switch data-testid={`ads-free-${c.id}`} checked={!!c.pricing?.ads_free} onCheckedChange={v => setAdsFree(c, v)} /> AD-FREE</label>
        </div>
      ))}
    </div>
  );
}

function SubsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.toolsSettings().then(setS); }, []);
  const save = async (patch) => { const next = { ...s, ...patch }; setS(next); try { await api.updateToolsSettings(patch); } catch { /* ignore */ } };
  if (!s) return <div className="mono text-white/40">LOADING…</div>;
  return (
    <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-5 max-w-xl space-y-4" data-testid="subs-tab">
      <div className="mono text-kinetic">SUBSCRIPTION SETTINGS</div>
      <p className="mono text-white/50">Let viewers subscribe to your artist page, channel or label. Manage renewal & notifications.</p>
      <div className="flex items-center justify-between"><div className="font-sans font-bold">Enable channel subscriptions</div><Switch data-testid="subs-enable" checked={s.subs_enabled !== false} onCheckedChange={v => save({ subs_enabled: v })} /></div>
      <div className="flex items-center justify-between"><div className="font-sans font-bold">Auto-renew monthly</div><Switch data-testid="subs-renew" checked={!!s.subs_auto_renew} onCheckedChange={v => save({ subs_auto_renew: v })} /></div>
      <div className="flex items-center justify-between"><div className="font-sans font-bold">Notify subscribers on new drops</div><Switch data-testid="subs-notify" checked={s.subs_notify !== false} onCheckedChange={v => save({ subs_notify: v })} /></div>
      <div className="mono text-white/40">Subscribe-to types supported: Artist · Channel · Label</div>
    </div>
  );
}

export default function Workstation() {
  const [stab, setStab] = useState("content");
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("upload");
  const [analytics, setAnalytics] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", logline: "", genre: "", channel: "", poster: "", credits: [] });
  const [credit, setCredit] = useState({ name: "", role: "", type: "artist" });
  const posterRef = useRef(null);
  const videoRef = useRef(null);

  const load = useCallback(async () => {
    const list = await api.myContent();
    setItems(list);
    if (sel) { const s = list.find(x => x.id === sel.id); if (s) setSel(s); }
  }, [sel]);
  useEffect(() => { api.myContent().then(setItems); }, []);

  const selectContent = async (c) => { setSel(c); setTab("upload"); setAnalytics(null); };

  useEffect(() => {
    if (tab === "analytics" && sel) api.contentAnalytics(sel.id).then(setAnalytics).catch(() => setAnalytics(null));
  }, [tab, sel]);

  const uploadPoster = async (e, isNew) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const r = await api.uploadImage(f);
      if (isNew) setForm(s => ({ ...s, poster: r.path }));
      else { await api.updateContent(sel.id, { poster: r.path }); await load(); toast.success("Poster updated"); }
    } catch { toast.error("Poster upload failed (max 6MB image)"); }
  };

  const createContent = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    const c = await api.createContent(form);
    setOpen(false);
    setForm({ title: "", logline: "", genre: "", channel: "", poster: "", credits: [] });
    const list = await api.myContent(); setItems(list); selectContent(c);
    toast.success("Content created — now upload a video");
  };

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f || !sel) return;
    setUploading(true); setProgress(0);
    try {
      const uploadId = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
      const total = Math.ceil(f.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = f.slice(i * CHUNK, (i + 1) * CHUNK);
        await api.uploadVideoChunk(uploadId, i, blob);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      await api.completeVideoUpload(uploadId, f.name, sel.id);
      await load(); toast.success("Video uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); setProgress(0); }
  };

  const togglePublish = async () => {
    if (!sel.video_path && !sel.published) return toast.error("Upload a video before publishing");
    await api.updateContent(sel.id, { published: !sel.published }); await load();
    toast.success(!sel.published ? "Published — live on Watch" : "Unpublished");
  };

  const setTier = async (key, patch) => {
    const pricing = { ...(sel.pricing || {}) };
    pricing[key] = { ...(pricing[key] || { enabled: false, price: 0 }), ...patch };
    const paidTiers = TIERS.some(([k]) => pricing[k]?.enabled);
    await api.updateContent(sel.id, { pricing, is_free: !paidTiers });
    await load();
  };
  const setPaidScreening = async (enabled) => {
    const pricing = { ...(sel.pricing || {}) }; pricing.paid_screening = { enabled };
    await api.updateContent(sel.id, { pricing }); await load();
  };

  const del = async (c) => { await api.deleteContent(c.id); if (sel?.id === c.id) setSel(null); const list = await api.myContent(); setItems(list); };

  const addCredit = () => { if (!credit.name.trim()) return; setForm(s => ({ ...s, credits: [...s.credits, credit] })); setCredit({ name: "", role: "", type: "artist" }); };

  return (
    <div className="space-y-6" data-testid="workstation">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="mono text-white/40">STREAMERS WORKSTATION · TELL & SELL</div>
          <h1 className="text-4xl font-extrabold tracking-tight mt-2">Studio</h1>
          <p className="text-white/60 mt-2">Upload your content and sell it directly from your own box office.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-content-btn" className="kinetic-btn rounded-sm"><Plus className="w-4 h-4 mr-2" /> New Content</Button></DialogTrigger>
          <DialogContent className="bg-[#0C0C0C] border-white/10 text-white rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-sans">New Content</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[["title", "Title"], ["logline", "Logline (short)"], ["genre", "Genre"], ["channel", "Channel name"]].map(([k, l]) => (
                <div key={k}><Label className="mono text-white/60">{l}</Label>
                  <Input data-testid={`content-${k}`} value={form[k]} onChange={(e) => setForm(s => ({ ...s, [k]: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
              ))}
              <div>
                <Label className="mono text-white/60">Poster</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input ref={posterRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPoster(e, true)} />
                  <Button data-testid="content-poster-btn" type="button" variant="outline" onClick={() => posterRef.current?.click()} className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10"><UploadCloud className="w-4 h-4 mr-2" />Upload poster</Button>
                  {form.poster && <img src={posterUrl(form.poster)} alt="" className="w-12 h-16 object-cover rounded-sm" />}
                </div>
              </div>
              <div>
                <Label className="mono text-white/60 flex items-center gap-2"><Users className="w-3 h-3" /> Credits (cast & crew)</Label>
                <div className="flex gap-2 mt-1">
                  <Input data-testid="credit-name" value={credit.name} onChange={(e) => setCredit(c => ({ ...c, name: e.target.value }))} placeholder="Name" className="bg-black border-white/15 rounded-sm" />
                  <Input data-testid="credit-role" value={credit.role} onChange={(e) => setCredit(c => ({ ...c, role: e.target.value }))} placeholder="Role" className="bg-black border-white/15 rounded-sm" />
                  <Button data-testid="credit-add" type="button" onClick={addCredit} className="kinetic-btn rounded-sm shrink-0">Add</Button>
                </div>
                {form.credits.map((cr, i) => <div key={i} className="mono text-white/50 mt-1">{cr.name} · {cr.role}</div>)}
              </div>
            </div>
            <DialogFooter><Button data-testid="content-submit" onClick={createContent} className="kinetic-btn rounded-sm">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-1 flex-wrap border-b border-white/10 pb-3">
        {STUDIO_TABS.map(([k, l, Icon]) => (
          <button key={k} data-testid={`studio-tab-${k}`} onClick={() => setStab(k)}
            className={`mono px-3 h-9 rounded-sm border inline-flex items-center gap-2 ${stab === k ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>

      {stab === "content" && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Content list */}
        <div className="space-y-2" data-testid="content-list">
          {items.length === 0 && <div className="border border-dashed border-white/15 rounded-lg p-10 text-center mono text-white/40">NO CONTENT YET</div>}
          {items.map(c => (
            <button key={c.id} data-testid={`content-item-${c.id}`} onClick={() => selectContent(c)}
              className={`w-full text-left flex items-center gap-3 border rounded-lg p-3 transition-colors ${sel?.id === c.id ? "border-kinetic bg-kinetic/5" : "border-white/10 hover:border-white/25 bg-[#0C0C0C]"}`}>
              <img src={posterUrl(c.poster) || "https://images.unsplash.com/photo-1572188863110-46d457c9234d?q=80&w=200"} alt="" className="w-10 h-14 object-cover rounded-sm bg-black" />
              <div className="min-w-0 flex-1">
                <div className="font-sans font-bold truncate">{c.title}</div>
                <div className="mono text-white/40">{c.published ? "PUBLISHED" : "DRAFT"} · {c.views || 0} views</div>
              </div>
              <Trash2 data-testid={`content-del-${c.id}`} onClick={(e) => { e.stopPropagation(); del(c); }} className="w-4 h-4 text-white/30 hover:text-coral" />
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="lg:col-span-2">
          {!sel ? (
            <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-16 text-center mono text-white/40">SELECT OR CREATE CONTENT TO EDIT</div>
          ) : (
            <div className="border border-white/10 bg-[#0C0C0C] rounded-lg">
              <div className="flex items-center justify-between p-4 border-b border-white/10 flex-wrap gap-3">
                <div className="font-sans font-bold text-lg">{sel.title}</div>
                <button data-testid="publish-toggle" onClick={togglePublish}
                  className={`mono px-3 h-8 rounded-sm border inline-flex items-center gap-2 ${sel.published ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/20 text-white/60 hover:text-white"}`}>
                  <Check className="w-3.5 h-3.5" /> {sel.published ? "PUBLISHED" : "PUBLISH"}
                </button>
              </div>
              <div className="flex gap-1 p-2 border-b border-white/10 overflow-x-auto">
                {TABS.map(([k, l, Icon]) => (
                  <button key={k} data-testid={`ws-tab-${k}`} onClick={() => setTab(k)}
                    className={`mono px-3 h-8 rounded-sm inline-flex items-center gap-2 whitespace-nowrap ${tab === k ? "text-kinetic bg-white/5" : "text-white/55 hover:text-white"}`}>
                    <Icon className="w-3.5 h-3.5" /> {l}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {tab === "upload" && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <img src={posterUrl(sel.poster) || "https://images.unsplash.com/photo-1572188863110-46d457c9234d?q=80&w=200"} alt="" className="w-16 h-24 object-cover rounded-sm bg-black" />
                      <div>
                        <input ref={posterRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPoster(e, false)} />
                        <Button data-testid="edit-poster-btn" variant="outline" onClick={() => posterRef.current?.click()} className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10"><UploadCloud className="w-4 h-4 mr-2" />Change poster</Button>
                      </div>
                    </div>
                    <div className="border border-dashed border-white/20 rounded-lg p-6 text-center">
                      <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={uploadVideo} />
                      <Film className="w-8 h-8 text-kinetic mx-auto mb-2" />
                      <div className="font-sans font-bold">{sel.video_path ? "Video uploaded ✓" : "No video yet"}</div>
                      <div className="mono text-white/40 mt-1">MP4 / WEBM / MOV · up to 500MB</div>
                      {uploading ? (
                        <div className="mt-4"><div className="h-2 bg-white/10 rounded-sm overflow-hidden"><div className="h-full bg-kinetic transition-all" style={{ width: `${progress}%` }} /></div><div className="mono text-kinetic mt-1">{progress}%</div></div>
                      ) : (
                        <Button data-testid="upload-video-btn" onClick={() => videoRef.current?.click()} className="kinetic-btn rounded-sm mt-4"><UploadCloud className="w-4 h-4 mr-2" />{sel.video_path ? "Replace video" : "Upload video"}</Button>
                      )}
                    </div>
                  </div>
                )}

                {tab === "pricing" && (
                  <div className="space-y-3" data-testid="pricing-editor">
                    <p className="mono text-white/50">Enable tiers and set INR (₹) prices. If none enabled, content is FREE.</p>
                    {TIERS.map(([k, l]) => {
                      const t = sel.pricing?.[k] || { enabled: false, price: 0 };
                      return (
                        <div key={k} className="flex items-center gap-3 border border-white/10 rounded-sm px-4 py-3">
                          <Switch data-testid={`price-toggle-${k}`} checked={!!t.enabled} onCheckedChange={(v) => setTier(k, { enabled: v })} />
                          <div className="flex-1 font-sans font-bold">{l}</div>
                          <div className="flex items-center gap-1">
                            <span className="mono text-white/40">₹</span>
                            <Input data-testid={`price-input-${k}`} type="number" value={t.price || ""} onChange={(e) => setTier(k, { price: Number(e.target.value) })} className="bg-black border-white/15 rounded-sm w-28" disabled={!t.enabled} />
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-3 border border-white/10 rounded-sm px-4 py-3">
                      <Switch data-testid="price-toggle-paid_screening" checked={!!sel.pricing?.paid_screening?.enabled} onCheckedChange={setPaidScreening} />
                      <div className="flex-1 font-sans font-bold">Allow paid-screening requests</div>
                    </div>
                  </div>
                )}

                {tab === "analytics" && (
                  <div data-testid="analytics-panel">
                    {!analytics ? <div className="mono text-white/40">LOADING…</div> : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-lg overflow-hidden">
                        {[["VIEWS", analytics.views], ["REVENUE", `₹${(analytics.revenue || 0).toLocaleString("en-IN")}`], ["PURCHASES", analytics.purchases], ["RATING", `${analytics.rating_avg}★`], ["RATINGS", analytics.rating_count], ["REVIEWS", analytics.reviews]].map(([l, v]) => (
                          <div key={l} className="bg-[#0E0E0E] p-5"><div className="mono text-white/40">{l}</div><div className="font-sans text-2xl font-extrabold mt-1">{v}</div></div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {tab === "settings" && (
                  <div className="space-y-3" data-testid="streaming-settings">
                    {[["title", "Title"], ["logline", "Logline"], ["genre", "Genre"], ["channel", "Channel"], ["description", "Synopsis"]].map(([k, l]) => (
                      <div key={k}><Label className="mono text-white/60">{l}</Label>
                        {k === "description"
                          ? <Textarea data-testid={`set-${k}`} defaultValue={sel[k] || ""} onBlur={(e) => api.updateContent(sel.id, { [k]: e.target.value }).then(load)} className="bg-black border-white/15 rounded-sm" />
                          : <Input data-testid={`set-${k}`} defaultValue={sel[k] || ""} onBlur={(e) => api.updateContent(sel.id, { [k]: e.target.value }).then(load)} className="bg-black border-white/15 rounded-sm" />}
                      </div>
                    ))}
                    <div className="grid grid-cols-3 gap-3">
                      {[["series_id", "Series ID"], ["season", "Season"], ["episode", "Episode"]].map(([k, l]) => (
                        <div key={k}><Label className="mono text-white/60">{l}</Label>
                          <Input data-testid={`set-${k}`} type={k === "series_id" ? "text" : "number"} defaultValue={sel[k] || ""} onBlur={(e) => api.updateContent(sel.id, { [k]: k === "series_id" ? e.target.value : Number(e.target.value) }).then(load)} className="bg-black border-white/15 rounded-sm" /></div>
                      ))}
                    </div>
                    <p className="mono text-white/40">Changes save on blur.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {stab === "playlists" && <PlaylistsTab items={items} />}
      {stab === "screenings" && <ScreeningsTab />}
      {stab === "ads" && <AdsTab items={items} reload={load} />}
      {stab === "subscriptions" && <SubsTab />}
    </div>
  );
}
